import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Overlay, Popover } from 'react-bootstrap';
import { useGetOrderListFilterOptionsQuery } from '../../../slices/ordersApiSlice';
import {
	getOrderListColumnFilterClearPatch,
	isOrderListColumnFilterActive,
	normalizeOrderListFilters,
} from '../../../utils/order/orderListFilters';
import {
	getOrderListDeliveryStatusFilterOptions,
	getOrderListPaymentStatusFilterOptions,
} from '../../../utils/order/orderListStatusFilterOptions';
import OrderListMultiSelectFilter from './OrderListMultiSelectFilter';

const TEXT_DEBOUNCE_MS = 320;

/** 用漏斗中心写死箭头 left，避免 adaptive/transform 把箭头算偏 */
const pinColumnFilterArrow = {
	name: 'pinColumnFilterArrow',
	enabled: true,
	phase: 'beforeWrite',
	fn({ state }) {
		const arrowEl = state.elements.arrow;
		if (!arrowEl) return;
		const base = String(state.placement || '').split('-')[0];
		if (base !== 'top' && base !== 'bottom') return;
		const arrowWidth = arrowEl.offsetWidth || 16;
		const min = 6;
		const max = Math.max(min, state.rects.popper.width - arrowWidth - 6);
		const left = state.rects.reference.x + state.rects.reference.width / 2 - state.rects.popper.x - arrowWidth / 2;
		state.styles.arrow = {
			...state.styles.arrow,
			position: 'absolute',
			left: `${Math.max(min, Math.min(left, max))}px`,
			right: 'auto',
			transform: 'none',
		};
	},
};

const COLUMN_FILTER_POPPER_CONFIG = {
	strategy: 'fixed',
	modifiers: [
		{ name: 'offset', options: { offset: [0, 10] } },
		{
			name: 'computeStyles',
			options: {
				adaptive: false,
				gpuAcceleration: false,
			},
		},
		{
			name: 'preventOverflow',
			options: {
				padding: 8,
				boundary: 'viewport',
				mainAxis: true,
				altAxis: true,
			},
		},
		{
			name: 'flip',
			options: {
				fallbackPlacements: ['top'],
				flipVariations: false,
				altAxis: false,
			},
		},
		{
			name: 'arrow',
			options: { padding: 6 },
		},
		pinColumnFilterArrow,
	],
};

const sameFilterValue = (filterKey, left, right) => {
	const a = normalizeOrderListFilters({ [filterKey]: left })[filterKey];
	const b = normalizeOrderListFilters({ [filterKey]: right })[filterKey];
	return JSON.stringify(a) === JSON.stringify(b);
};

const isInsideSelectMenu = (target) =>
	target instanceof Element && Boolean(target.closest?.('.react-select__menu-portal, .react-select__menu, .react-select__menu-notice'));

/** 列头标题 + 漏斗浮层（改值后 debounce 再搜，聚焦不请求；全表同时仅一个弹层） */
const OrderListColumnFilterHeader = ({ table, column, label, filterKey, filterType = 'text', placeholder }) => {
	const applied = table?.options?.meta?.columnFilterApplied ?? {};
	const onColumnFilterPatch = table?.options?.meta?.onColumnFilterPatch;
	const openColumnFilterKey = table?.options?.meta?.openColumnFilterKey ?? null;
	const setOpenColumnFilterKey = table?.options?.meta?.setOpenColumnFilterKey;
	const enabled = typeof onColumnFilterPatch === 'function' && typeof setOpenColumnFilterKey === 'function';
	const canSort = Boolean(column?.getCanSort?.());

	const show = enabled && openColumnFilterKey === filterKey;
	const setShow = useCallback(
		(next) => {
			if (!enabled) return;
			const shouldOpen = typeof next === 'function' ? next(show) : Boolean(next);
			setOpenColumnFilterKey(shouldOpen ? filterKey : null);
		},
		[enabled, filterKey, setOpenColumnFilterKey, show],
	);

	const [textValue, setTextValue] = useState('');
	const anchorRef = useRef(null);
	const panelRef = useRef(null);
	const overlayRefFromProps = useRef(null);
	const popperScheduleUpdateRef = useRef(null);
	const debounceRef = useRef(null);
	const isComposingRef = useRef(false);
	const appliedValueRef = useRef(applied?.[filterKey]);
	const panelId = useId();

	const schedulePopoverPositionUpdate = useCallback(() => {
		const update = popperScheduleUpdateRef.current;
		if (typeof update !== 'function') return;
		requestAnimationFrame(() => {
			update();
		});
	}, []);

	const setPopoverRef = useCallback(
		(node) => {
			panelRef.current = node;
			const overlayRef = overlayRefFromProps.current;
			if (typeof overlayRef === 'function') overlayRef(node);
			else if (overlayRef) overlayRef.current = node;
			if (node) schedulePopoverPositionUpdate();
		},
		[schedulePopoverPositionUpdate],
	);

	const isActive = enabled && isOrderListColumnFilterActive(applied, filterKey);
	const appliedValue = applied?.[filterKey];
	const needsRemoteOptions = filterType === 'customer' || filterType === 'sale' || filterType === 'assistant';

	appliedValueRef.current = appliedValue;

	useEffect(() => {
		if (!show || filterType !== 'text') return;
		setTextValue(typeof appliedValue === 'string' ? appliedValue : '');
	}, [appliedValue, filterType, show]);

	useEffect(() => {
		if (!show) return undefined;
		const onDocMouseDown = (event) => {
			const target = event.target;
			if (!(target instanceof Element)) return;
			if (anchorRef.current?.contains(target)) return;
			if (panelRef.current?.contains(target)) return;
			if (isInsideSelectMenu(target)) return;
			/* 点其他列漏斗时由对方 toggle 切换，避免先关再开 */
			if (target.closest?.('.order-list-column-filter-header__btn')) return;
			setShow(false);
		};
		const onKeyDown = (event) => {
			if (event.key === 'Escape') setShow(false);
		};
		document.addEventListener('mousedown', onDocMouseDown);
		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('mousedown', onDocMouseDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [setShow, show]);

	useEffect(
		() => () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		},
		[],
	);

	useEffect(() => {
		if (show) return undefined;
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
			debounceRef.current = null;
		}
		return undefined;
	}, [show]);

	const { data: filterOptionsData, isLoading: isLoadingFilterOptions } = useGetOrderListFilterOptionsQuery(undefined, {
		skip: !show || !needsRemoteOptions,
	});

	/* 选值/筛完后表格列宽会变，漏斗位置跟着动，需重算箭头 */
	useLayoutEffect(() => {
		if (!show) return undefined;
		const panel = panelRef.current;
		const anchor = anchorRef.current;
		const headerCell = anchor?.closest?.('th');
		const table = anchor?.closest?.('table');
		const onScrollOrResize = () => schedulePopoverPositionUpdate();
		let resizeObserver;
		if (typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(onScrollOrResize);
			if (panel) resizeObserver.observe(panel);
			if (anchor) resizeObserver.observe(anchor);
			if (headerCell) resizeObserver.observe(headerCell);
			if (table) resizeObserver.observe(table);
		}
		window.addEventListener('scroll', onScrollOrResize, true);
		window.addEventListener('resize', onScrollOrResize);
		schedulePopoverPositionUpdate();
		return () => {
			resizeObserver?.disconnect();
			window.removeEventListener('scroll', onScrollOrResize, true);
			window.removeEventListener('resize', onScrollOrResize);
		};
	}, [appliedValue, isLoadingFilterOptions, schedulePopoverPositionUpdate, show]);

	const multiOptions = useMemo(() => {
		if (filterType === 'paymentStatus') return getOrderListPaymentStatusFilterOptions();
		if (filterType === 'deliveryStatus') return getOrderListDeliveryStatusFilterOptions();
		return undefined;
	}, [filterType]);

	const multiValues = useMemo(() => {
		if (filterType === 'customer') return filterOptionsData?.customerNames ?? [];
		if (filterType === 'sale') return filterOptionsData?.saleUserNames ?? [];
		if (filterType === 'assistant') return filterOptionsData?.assistantUserNames ?? [];
		return undefined;
	}, [filterOptionsData, filterType]);

	const patchIfChanged = useCallback(
		(nextValue) => {
			if (!enabled) return;
			if (sameFilterValue(filterKey, appliedValueRef.current, nextValue)) return;
			onColumnFilterPatch({ [filterKey]: nextValue });
		},
		[enabled, filterKey, onColumnFilterPatch],
	);

	const handleTextChange = (event) => {
		const next = event.target.value;
		setTextValue(next);
		/* 拼音组字中不触发筛选，避免把未上屏字母当关键词 */
		if (isComposingRef.current || event.nativeEvent?.isComposing) {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
				debounceRef.current = null;
			}
			return;
		}
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			debounceRef.current = null;
			patchIfChanged(next);
		}, TEXT_DEBOUNCE_MS);
	};

	const handleTextCompositionStart = () => {
		isComposingRef.current = true;
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
			debounceRef.current = null;
		}
	};

	const handleTextCompositionEnd = (event) => {
		isComposingRef.current = false;
		const next = event.currentTarget?.value ?? event.target?.value ?? '';
		setTextValue(next);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			debounceRef.current = null;
			patchIfChanged(next);
		}, TEXT_DEBOUNCE_MS);
	};

	const commitTextFilter = useCallback(
		(rawValue) => {
			const next = String(rawValue ?? '');
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
				debounceRef.current = null;
			}
			setTextValue(next);
			patchIfChanged(next);
		},
		[patchIfChanged],
	);

	const handleTextKeyDown = (event) => {
		if (event.key !== 'Enter') return;
		if (isComposingRef.current || event.nativeEvent?.isComposing) return;
		event.preventDefault();
		commitTextFilter(event.currentTarget.value);
	};

	const handleMultiChange = (values) => {
		patchIfChanged(values);
		schedulePopoverPositionUpdate();
	};

	const handleClear = () => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
			debounceRef.current = null;
		}
		if (filterType === 'text') setTextValue('');
		if (!enabled) return;
		onColumnFilterPatch(getOrderListColumnFilterClearPatch(filterKey));
	};

	const stopReactTreeBubble = (event) => {
		event.stopPropagation();
	};

	const handleToggle = (event) => {
		event.preventDefault();
		event.stopPropagation();
		if (!enabled) return;
		setShow((prev) => !prev);
	};

	const handleLabelSort = (event) => {
		event.preventDefault();
		event.stopPropagation();
		if (!canSort) return;
		column.getToggleSortingHandler()?.(event);
	};

	const resolvedPlaceholder = placeholder ?? `筛选${label}`;
	const clearDisabled = filterType === 'text' ? !String(textValue || '').trim() && !isActive : !isActive;

	return (
		<>
			<span className='order-list-column-filter-header'>
				<span
					className={classNames('order-list-column-filter-header__label', {
						'is-sortable': canSort,
						sort: canSort,
						desc: canSort && column.getIsSorted() === 'desc',
						asc: canSort && column.getIsSorted() === 'asc',
					})}
					onClick={handleLabelSort}
					onMouseDown={canSort ? stopReactTreeBubble : undefined}
					role={canSort ? 'button' : undefined}
					tabIndex={canSort ? 0 : undefined}
					title={canSort ? `按${label}排序` : undefined}
					onKeyDown={
						canSort
							? (event) => {
									if (event.key === 'Enter' || event.key === ' ') {
										handleLabelSort(event);
									}
								}
							: undefined
					}
				>
					{label}
				</span>
				{enabled ? (
					<button
						ref={anchorRef}
						type='button'
						className={classNames('order-list-column-filter-header__btn', {
							'is-active': isActive,
							'is-open': show,
						})}
						aria-label={isActive ? `${label}列筛选（已生效）` : `${label}列筛选`}
						aria-expanded={show}
						aria-controls={panelId}
						title={isActive ? `${label}筛选（已生效）` : `${label}筛选`}
						onClick={handleToggle}
						onMouseDown={(event) => {
							event.preventDefault();
							event.stopPropagation();
						}}
						onMouseUp={stopReactTreeBubble}
					>
						<FontAwesomeIcon icon='filter' />
					</button>
				) : null}
			</span>
			{enabled ? (
				<Overlay
					show={show}
					target={anchorRef}
					placement='bottom'
					flip
					transition={false}
					popperConfig={COLUMN_FILTER_POPPER_CONFIG}
					container={typeof document !== 'undefined' ? document.body : undefined}
				>
					{(overlayProps) => {
						const { ref: overlayRef, arrowProps, className: overlayClassName, popper, ...restOverlayProps } = overlayProps;
						overlayRefFromProps.current = overlayRef;
						popperScheduleUpdateRef.current = popper?.scheduleUpdate ?? null;
						return (
							<Popover
								{...restOverlayProps}
								popper={popper}
								arrowProps={arrowProps}
								id={panelId}
								role='dialog'
								aria-label={`${label}筛选`}
								ref={setPopoverRef}
								className={classNames(overlayClassName, 'order-list-column-filter-popover')}
								onClick={stopReactTreeBubble}
								onMouseDown={stopReactTreeBubble}
								onMouseUp={stopReactTreeBubble}
								onPointerDown={stopReactTreeBubble}
							>
								<Popover.Body className='order-list-column-filter-popover__body'>
									{filterType === 'text' ? (
										<div className='order-list-column-filter-text-field'>
											<Form.Control
												size='sm'
												value={textValue}
												placeholder={resolvedPlaceholder}
												onChange={handleTextChange}
												onCompositionStart={handleTextCompositionStart}
												onCompositionEnd={handleTextCompositionEnd}
												onKeyDown={handleTextKeyDown}
												aria-label={resolvedPlaceholder}
											/>
											{String(textValue).trim() ? (
												<kbd className='order-list-column-filter-enter-badge' title='按 Enter 查询'>
													Enter
												</kbd>
											) : null}
										</div>
									) : (
										<OrderListMultiSelectFilter
											inputId={`orderListColumnFilter-${filterKey}`}
											label={label}
											options={multiOptions}
											values={multiValues}
											selected={Array.isArray(appliedValue) ? appliedValue : []}
											onChange={handleMultiChange}
											isLoading={needsRemoteOptions && isLoadingFilterOptions}
											maxVisibleChips={1}
											showLoadingIndicator
											menuInPlace
											showEnterHint
											placeholder={resolvedPlaceholder}
										/>
									)}
									<div className='order-list-column-filter-popover__footer'>
										<Button type='button' size='sm' variant='link' className='px-0' onClick={handleClear} disabled={clearDisabled}>
											清空
										</Button>
									</div>
								</Popover.Body>
							</Popover>
						);
					}}
				</Overlay>
			) : null}
		</>
	);
};

export default OrderListColumnFilterHeader;
